import { Box, Checkbox, Chip, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ImageInfo from '~/components/InfoTooltip/ImageInfo';
import { getModalityIcon } from '~/domain/dicom/modality';
import { Patient } from '~/domain/patient/patient';
import { DatasetTree } from '.';
import { ActiveItem } from '../../useActiveItems';
import { SelectedDatasets } from '../LoadedPatientDataTree';
import RegistrationLeaf from './RegistrationLeaf';
import StructureSetLeaf from './StructureSetLeaf';

interface ImageTreeProps {
    dataset: DatasetTree;
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onSelect: React.Dispatch<React.SetStateAction<SelectedDatasets>>;
    patient: Patient;
}

const ImageTree = ({
    dataset,
    selectedDatasets,
    activeItems,
    onSelect,
    patient,
}: ImageTreeProps) => {
    if (dataset?.image === undefined) return null;
    const { image } = dataset;

    const isImageActive = activeItems.some(
        (item) => item.dicomUid === image.dicomUid
    );
    const isImageSelectedAsPrimary =
        selectedDatasets?.image?.dicomUid === image.dicomUid;

    const isImageSelectedAsBlend =
        selectedDatasets?.blendImage?.dicomUid === image.dicomUid;

    const handleSelect = () => {
        onSelect({
            image: isImageSelectedAsPrimary ? undefined : image,
        });
    };

    return (
        <Box
            key={image.dicomUid}
            sx={{
                cursor: 'pointer',
            }}
            display="grid"
        >
            <Box
                display="flex"
                justifyContent="space-between"
                py={0.5}
                px={1}
                onClick={handleSelect}
                sx={(theme) => ({
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                    ...(isImageActive && {
                        backgroundColor: 'rgba(0, 165, 185, 0.1)',
                    }),
                })}
            >
                <Box display="flex" gap={0.5}>
                    <Checkbox
                        disableRipple
                        size="small"
                        color="primary"
                        checked={
                            isImageSelectedAsPrimary || isImageSelectedAsBlend
                        }
                        sx={{
                            '&': {
                                padding: 0,
                            },
                        }}
                    />
                    <Box>
                        {getModalityIcon(image.seriesMetadata.modality, 12)}
                    </Box>
                    <Typography display="flex" alignItems="center">
                        {image.firstSlice.modality}
                    </Typography>
                    <Typography
                        display="flex"
                        alignItems="center"
                        variant="body2"
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {image.seriesMetadata.seriesDescription}
                    </Typography>
                    <Typography color="primary">
                        <ImageInfo image={image} />
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                        display="flex"
                        alignItems="center"
                        variant="body2"
                    >
                        {image.seriesMetadata.seriesDate?.toDateString()}
                    </Typography>
                    {isImageActive && (
                        <Chip
                            variant="outlined"
                            size="small"
                            sx={{
                                fontSize: 10,
                                height: 20,
                                padding: 0.5,
                                lineHeight: 0.9,
                            }}
                            color="success"
                            label={<FormattedMessage id="active" />}
                        />
                    )}
                </Box>
            </Box>

            {dataset?.structureSets.map((ss) => (
                <StructureSetLeaf
                    key={ss.data.dicomUid}
                    image={image}
                    structureSet={ss.data}
                    doses={ss.doses}
                    selectedDatasets={selectedDatasets}
                    activeItems={activeItems}
                    onSelect={onSelect}
                />
            ))}

            {dataset?.registrations.map((reg) => (
                <RegistrationLeaf
                    key={reg.dicomUid}
                    image={image}
                    registration={reg}
                    selectedDatasets={selectedDatasets}
                    activeItems={activeItems}
                    onSelect={onSelect}
                    patient={patient}
                />
            ))}
        </Box>
    );
};

export default ImageTree;
